import { useEffect, useContext, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import ProcessContext from '../../contexts/process/ProcessContext';
import taskApiService from '../../services/apiServices/TasksApiService';
import Loading from '../ui-elements/Loading';
import UIContext from '../../contexts/ui/UIContext';
import PlantTaskNotFound from '../plant-tasks/PlantTaskNotFound';
import PlantTaskNotExecutable from '../plant-tasks/PlantTaskNotExecutable';
import AuthContext from '../../contexts/auth/AuthContext';

const ProcessStart = () => {
	const { startProcess, setTaskData, taskData, notifyError } = useContext(ProcessContext);
	const { accessibleTenants, accessibleContainers, currentTenant, currentContainer, setCurrentTenant, setCurrentContainer } = useContext(AuthContext);
	const { setInfo } = useContext(UIContext);
	const [searchParams] = useSearchParams();
	const navigate = useNavigate();
	const [message, setMessage] = useState('This plant task can not be found.');

	const taskId = searchParams.get('taskId');
	const redirectUrlRaw = searchParams.get('redirectUrl');
	const redirectUrl = redirectUrlRaw && redirectUrlRaw.startsWith('http') ? redirectUrlRaw : null;
	const [executionContext, setExecutionContext] = useState(null);

	const [loading, setLoading] = useState(true);

	// Reload accessible tenants when the component mounts
	useEffect(() => {
		setInfo('Start a Process');
		// If taskId is not available, show an error toast and return
		if (!taskId) {
			handleError(null);
			return;
		}
	}, []);

	// Initialize the execution context asynchronously
	useEffect(() => {
		const getExecutionContextAsync = async () => {
			// If taskId is not available, do nothing
			if (!taskId) return;
			try {
				const { data: executionContextData } = await taskApiService.getTaskExecutionContextAsync(taskId);
				setExecutionContext(executionContextData.data);
			} catch (error) {
				handleError(error);
			}
		};
		getExecutionContextAsync();
	}, [taskId]);

	// Set the current tenant and container based on the execution context
	useEffect(() => {
		// If the execution context is not set, do nothing
		if (!executionContext) return;

		// If the current container is the same as the execution context container, no need to set it again
		if (isCurrentTenantAndContainerMatchingTaskContext()) {
			return;
		}

		const setCurrentTenantAndContainerAsync = async () => {
			if (currentTenant?.id !== executionContext?.tenantId) {
				var tenant = accessibleTenants.find((tenant) => tenant.id === executionContext?.tenantId);
				await setCurrentTenant(tenant, executionContext?.containerId);
			} else {
				await setCurrentContainer(accessibleContainers.find((container) => container.id === executionContext?.containerId));
			}
		};
		setCurrentTenantAndContainerAsync();
	}, [executionContext, accessibleTenants]);

	useEffect(() => {
		const handleTaskDataAsync = async () => {
			if (!taskId) {
				setLoading(false);
				return;
			}

			setTaskData({ redirectUrl });
			try {
				const { data: prepareTaskData } = await taskApiService.prepareTaskAsync(currentTenant.id, currentContainer.id, taskId);
				var task = prepareTaskData?.data;

				setTaskData({ ...task, redirectUrl });

				if (task?.taskCoverage !== 'None') {
					await startProcess(task?.metaData?.processType, taskId);
					navigate(`/${task?.metaData?.processType.toLowerCase()}`);
				}
			} catch (error) {
				handleError(error);
			} finally {
				setLoading(false);
			}
		};
		if (executionContext == null || !isCurrentTenantAndContainerMatchingTaskContext()) return;

		handleTaskDataAsync();
	}, [currentTenant, currentContainer, executionContext]);

	useEffect(() => {
		if (loading) return;
		if (taskData?.taskCoverage === 'None') {
			setInfo('Plant Task currently not executable');
		}
	}, [loading, taskData]);

	const handleDeleteAsync = async () => {
		try {
			await taskApiService.deleteTaskAsync(currentTenant.id, currentContainer.id, taskId);
			handleBack();
		} catch {
			notifyError('Deletion failed');
		}
	};

	const handleBack = () => {
		if (taskData?.redirectUrl) {
			window.location.href = taskData.redirectUrl;
		} else {
			navigate('/', { state: { slide: 1 } });
		}
	};

	const isCurrentTenantAndContainerMatchingTaskContext = () => {
		return currentContainer?.id === executionContext?.containerId && currentTenant?.id === executionContext?.tenantId;
	};

	// Handle errors based on the status code
	const handleError = (error) => {
		setLoading(false);
		const status = error.response?.data?.code;
		if (status) {
			switch (status) {
				case 401:
				case 403:
					setMessage('You are not authorized to query plant-task-related information for this plant task.');
					setInfo('Not authorized');
					break;
				case 404:
					setInfo('Plant Task not found');
					break;
				case 409:
					setMessage('Container not found.');
					setInfo('Container not found');
					break;
				case 500:
					setMessage('Plant task could not be prepared.');
					notifyError('Internal server error.');
					break;
				default:
					setMessage('Plant task could not be prepared.');
					notifyError(`Error fetching task data.`);
					break;
			}
		} else {
			setMessage('Plant task could not be prepared.');
			setInfo('Plant Task not found');
		}
	};

	return (
		<>
			{loading && <Loading fullScreen />}

			{!loading && !taskData?.id && <PlantTaskNotFound displayText={message} onBack={handleBack} />}

			{!loading && taskData?.id && taskData?.taskCoverage === 'None' && <PlantTaskNotExecutable onDelete={handleDeleteAsync} />}
		</>
	);
};

export default ProcessStart;
